import { FC } from 'react'
import { Link } from 'react-router-dom'
import {
  getBundleRelationVariants,
  getCurrentListingCardEyebrow,
  getLinkedAvailablePlatformId,
  getLinkedPlatformLabelForCopy,
  getLinkedProductCardEyebrow,
  normalizeSku,
  OrderedParentDetailRow,
  ProductLinkingContext,
  resolveBundleComponentsFromRelation,
} from './productLinkingDetailUtils'

const linkIconSvg = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M10 13a5 5 0 007.07 0l1.41-1.41a5 5 0 00-7.07-7.07L10 5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M14 11a5 5 0 00-7.07 0L5.52 12.41a5 5 0 007.07 7.07L14 19"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
)

const SkuChip: FC<{ sku: string }> = ({ sku }) => (
  <span
    style={{
      display: 'inline-block',
      marginTop: 8,
      padding: '4px 10px',
      borderRadius: 6,
      background: '#f1f3f5',
      fontSize: 12,
      fontWeight: 600,
      color: '#495057',
    }}
  >
    SKU {sku || '—'}
  </span>
)

const ProductCard: FC<{
  eyebrow: string
  title: string
  sku: string
  accent?: 'channel' | 'oms'
  skuLink?: string
}> = ({ eyebrow, title, sku, accent = 'channel', skuLink }) => (
  <div
    style={{
      flex: 1,
      minWidth: 0,
      border: '1px solid #e9ecef',
      borderRadius: 10,
      padding: '16px 18px',
      background: '#fff',
    }}
  >
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.06em',
        color: '#198754',
        marginBottom: 10,
      }}
    >
      {eyebrow}
    </div>
    <div style={{ fontSize: 14, fontWeight: 600, color: '#212529', lineHeight: 1.45 }}>{title || '—'}</div>
    {skuLink ? (
      <Link to={skuLink} className="text-info text-decoration-none">
        <SkuChip sku={sku} />
      </Link>
    ) : (
      <SkuChip sku={sku} />
    )}
  </div>
)

const LinkConnector: FC = () => (
  <div
    style={{
      flex: '0 0 56px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
    }}
  >
    <div
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        top: '50%',
        height: 2,
        background: '#86efac',
        transform: 'translateY(-50%)',
      }}
    />
    <div
      style={{
        position: 'relative',
        zIndex: 1,
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: '#22c55e',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 0 0 4px #fff',
      }}
    >
      {linkIconSvg}
    </div>
  </div>
)

const StatusBadge: FC<{ linked: boolean }> = ({ linked }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '4px 12px',
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600,
      background: linked ? '#ecfdf3' : '#f1f3f5',
      color: linked ? '#15803d' : '#6c757d',
      border: linked ? '1px solid #bbf7d0' : '1px solid #dee2e6',
    }}
  >
    <span style={{ display: 'inline-flex', opacity: 0.85 }}>{linkIconSvg}</span>
    {linked ? 'Linked' : 'Not linked'}
  </span>
)

type NonOmsLinkingStatusSectionProps = {
  channelListingName: string
  channelListingSku: string
  channelPlatformName: string
  linkingRows: OrderedParentDetailRow[]
  linkingContext: ProductLinkingContext
  fetchedConnectedPlatformData: any[]
  platformId?: string
  availablePlatformId?: string
  hasVariantsData: boolean
  /** Main/source store display name for not-linked copy (e.g. Shopify). */
  sourcePlatformName?: string
}

const LinkedBlock: FC<
  NonOmsLinkingStatusSectionProps & {
    row: OrderedParentDetailRow
  }
> = ({
  row,
  channelListingName,
  channelListingSku,
  channelPlatformName,
  linkingContext,
  fetchedConnectedPlatformData,
  platformId,
  availablePlatformId,
  hasVariantsData,
}) => {
  const { item } = row
  const bundleRelations = getBundleRelationVariants(item, linkingContext)
  const bundleComponents =
    bundleRelations.length > 0
      ? resolveBundleComponentsFromRelation(bundleRelations)
      : resolveBundleComponentsFromRelation(item?.bundle_relation_data || [])

  const isBundle = bundleComponents.length > 0
  const linkedTarget = item?.isFullBundleRelationView ? null : item
  const targetName =
    linkedTarget?.name ||
    linkedTarget?.platform_product?.name ||
    (isBundle ? channelListingName : '')
  const targetSku = linkedTarget?.sku || ''
  const linkedProductEyebrow = linkedTarget
    ? getLinkedProductCardEyebrow(linkedTarget, fetchedConnectedPlatformData)
    : getLinkedProductCardEyebrow(item, fetchedConnectedPlatformData)

  const linkedAvailablePlatformId = linkedTarget
    ? getLinkedAvailablePlatformId(linkedTarget, fetchedConnectedPlatformData, availablePlatformId)
    : ''
  const canLinkSku =
    hasVariantsData &&
    linkedTarget?.id &&
    linkedTarget?.platform_product?.platform &&
    linkedTarget?.platform_product?.id &&
    linkedAvailablePlatformId

  const skuMismatch =
    !isBundle &&
    channelListingSku &&
    targetSku &&
    normalizeSku(channelListingSku) !== normalizeSku(targetSku)

  const sourcePlatform = fetchedConnectedPlatformData?.find(
    (platform) => Number(platform?.id) === Number(platformId)
  )
  const listingEyebrow = getCurrentListingCardEyebrow(channelPlatformName, sourcePlatform)
  const linkedPlatformLabel = getLinkedPlatformLabelForCopy(
    linkedTarget ?? item,
    fetchedConnectedPlatformData,
    'source'
  )

  return (
    <div className="mb-4 pb-4 border-bottom border-gray-200">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3 mt-3">
        <StatusBadge linked />
        {isBundle && (
          <span className="text-muted fs-7 fw-semibold">
            Bundle of {bundleComponents.length} SKU{bundleComponents.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {(!isBundle || linkedTarget || item?.isFullBundleRelationView) && (
        <div className="d-flex flex-column flex-lg-row align-items-stretch gap-2 mb-3">
          <ProductCard
            eyebrow={listingEyebrow}
            title={channelListingName}
            sku={channelListingSku}
            accent="channel"
          />
          {linkedTarget && !item?.isFullBundleRelationView ? (
            <>
              <LinkConnector />
              <ProductCard
                eyebrow={linkedProductEyebrow}
                title={targetName}
                sku={targetSku}
                accent="oms"
                skuLink={
                  canLinkSku
                    ? `/product-variant-detail/${linkedTarget.id}/platform/${linkedTarget.platform_product.platform}/product/${linkedTarget.platform_product.id}/${linkedAvailablePlatformId}`
                    : undefined
                }
              />
            </>
          ) : null}
        </div>
      )}

      {skuMismatch && (
        <div
          className="mb-3 px-3 py-2 rounded"
          style={{ background: '#fff8e6', border: '1px solid #ffe08a', fontSize: 13, color: '#664d03' }}
        >
          Listing SKU <strong>{channelListingSku}</strong> is linked to a different{' '}
          {linkedPlatformLabel} SKU <strong>{targetSku}</strong>.
        </div>
      )}

      {isBundle && (
        <>
          <div className="text-muted fs-7 fw-semibold mb-2">1 sale deducts {bundleComponents.length} SKUs</div>
          <div
            style={{
              border: '1px solid #e9ecef',
              borderRadius: 10,
              overflow: 'hidden',
              marginBottom: 12,
            }}
          >
            <div
              className="px-3 py-2 fw-bold fs-7 text-uppercase"
              style={{ background: '#f8f9fa', color: '#6c757d' }}
            >
              {linkedPlatformLabel} components
            </div>
            {bundleComponents.map((component, index) => {
              const covers =
                component.availableQuantity != null && component.quantity > 0
                  ? Math.floor(Number(component.availableQuantity) / Number(component.quantity))
                  : null
              return (
                <div
                  key={component.id ?? index}
                  className="px-3 py-3 d-flex flex-wrap justify-content-between gap-2"
                  style={{
                    borderTop: index === 0 ? undefined : '1px solid #e9ecef',
                    fontSize: 13,
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="fw-semibold">{component.name || '—'}</div>
                    <div className="text-muted">SKU {component.sku || '—'}</div>
                  </div>
                  <div className="text-end text-muted">
                    <div>Qty {component.quantity == null ? '—' : component.quantity}</div>
                    {covers != null && <div>{covers} bundles</div>}
                  </div>
                </div>
              )
            })}
          </div>
          {!item?.isFullBundleRelationView && platformId && (
            <div className="fs-7">
              <Link to={`/bundles?platform_id=${platformId}`} className="text-info text-decoration-none">
                See full bundle relation details
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export const NonOmsLinkingStatusSection: FC<NonOmsLinkingStatusSectionProps> = (props) => {
  const { linkingRows, channelPlatformName, sourcePlatformName = 'source' } = props
  const isLinked = linkingRows.length > 0

  return (
    <div className="card card-flush">
      <div className="card-headerP">
        <div className="card-titleP d-flex flex-wrap align-items-center gap-3">
          <h2 className="mb-0">Linking Status</h2>
          {!isLinked && <StatusBadge linked={false} />}
        </div>
      </div>
      <div className="card-bodyP pt-0">
        {!isLinked ? (
          <div
            style={{
              border: '2px dashed #dee2e6',
              borderRadius: 12,
              padding: '20px 22px',
              background: '#fafbfc',
            }}
          >
            <p className="mb-2 fw-semibold" style={{ fontSize: 15, color: '#212529' }}>
              This {channelPlatformName || 'channel'} listing isn&apos;t connected to any{' '}
              {sourcePlatformName} product.
            </p>
            <p className="mb-0 text-muted fs-7">
              Orders from it won&apos;t deduct {sourcePlatformName} inventory and its stock won&apos;t be synced.
              Link it from the
              listing workflow when you&apos;re ready to connect this product.
            </p>
          </div>
        ) : (
          linkingRows.map((row) => (
            <LinkedBlock key={`${row.item?.type}-${row.item?.id ?? row.index}`} {...props} row={row} />
          ))
        )}
      </div>
    </div>
  )
}
